import { useRef } from "react";
import { css, styled } from "styled-components";

import { CrossIcon, UIButton } from "common/components";
import { zIndex } from "common/constants/zIndex";
import { cssResets } from "common/styles/cssResets";
import { safelyToUrl } from "common/utilities/safelyToUrl";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { CALL_TO_ACTION_MAX_WIDTH } from "domains/callsToAction/helpers/calculateCallToActionPositions";
import type { CutoutProps, PopupProps } from "domains/callsToAction/hooks/useCallToAction";
import { useCallToAction } from "domains/callsToAction/hooks/useCallToAction";
import { CallToActionOverlay } from "domains/callsToAction/parts/CallToActionOverlay";
import type { CallToActionStory } from "domains/callsToAction/types";

type CallToActionProps = { callToAction: CallToActionStory; onClose: () => void; target: Element };

export const CallToAction = ({ callToAction, onClose, target }: CallToActionProps) => {
  const go = useGo();
  const containerRef = useRef<HTMLDivElement>(null);
  const track = useTrack();
  const translate = useTranslate();

  const { cutout, popup, viewport } = useCallToAction(containerRef.current, target, callToAction.highlight_element);

  const onClickUpgrade = () =>
    track({ entityId: callToAction.id, entityName: `[Call to Action] ${callToAction.title}`, name: "Upgrade" });

  const destinationButtonOnClick = (item: (typeof callToAction.destinations)[number]) => () => {
    if (item.label.includes("upgrade")) onClickUpgrade();
    const url = safelyToUrl(item.url);
    if (url === undefined) return;
    if (url.hostname.endsWith("intelligence.weforum.org")) go(`${url.pathname}${url.search}${url.hash}`);
    else go(item.url);
  };

  return (
    <>
      <Container $popup={popup} ref={containerRef}>
        <Title>{callToAction.title}</Title>
        <Subtitle>{translate("app.about.key_points.membership_feature")}</Subtitle>
        <Description>{callToAction.description}</Description>
        <Actions>
          {callToAction.destinations.map((item) => (
            <UIButton
              {...(item.type === "link" && !item.url.includes("intelligence.weforum.org")
                ? { as: "a", href: item.url, onClick: item.label.includes("upgrade") ? onClickUpgrade : undefined }
                : { as: "button", onClick: destinationButtonOnClick(item) })}
              key={item.url}
              variant={item.role === "primary" ? "filled" : "neutral"}
            >
              {translate(item.label)}
            </UIButton>
          ))}
        </Actions>
        <Images>
          <Image src={callToAction.images.cover} alt={callToAction.title} />
          <Image src={callToAction.images.inside} alt={callToAction.title} />
        </Images>
        <CloseButton onClick={onClose}>
          <CloseButtonIcon />
        </CloseButton>
      </Container>
      <CallToActionOverlay>
        <Mask id="call-to-action-mask">
          <rect height={viewport.height} width={viewport.width} />
          <Cutout cx={cutout.x} cy={cutout.y} r={cutout.radius} />
        </Mask>
        <rect height={viewport.height} mask="url(#call-to-action-mask)" width={viewport.width} />
      </CallToActionOverlay>
      <Triangle $cutout={cutout} $popup={popup} />
    </>
  );
};

const Container = styled.aside<{ $popup?: PopupProps }>`
  align-items: center;
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  padding: 2rem;
  position: absolute;
  width: ${CALL_TO_ACTION_MAX_WIDTH}px;
  z-index: ${zIndex.coachmarkActive};

  ${({ theme }) => css`
    background-color: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${theme.colors.fillPrimary};
  `}

  ${({ $popup }) =>
    $popup !== undefined &&
    css`
      left: ${$popup.left}px;
      max-height: 100vh;
      top: ${$popup.top}px;
      width: ${$popup.width}px;
    `}
`;

const Title = styled.h1`
  ${cssResets.h1}
  color: ${({ theme }) => theme.colors.textBright};
  font-size: 1.5rem;
  font-weight: 900;
`;

const Subtitle = styled.h2`
  ${cssResets.h2}
  background-color: ${({ theme }) => theme.colors.tagBackground};
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.colors.white};
  font-size: 1rem;
  font-weight: bold;
  padding: 0.125rem 0.5rem;
  text-transform: uppercase;
  width: fit-content;
`;

const Description = styled.p`
  ${cssResets.p}
  color: ${({ theme }) => theme.colors.textFade};
  font-size: 0.9rem;
  line-height: 1.3rem;
  text-align: center;
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
`;

const Images = styled.div`
  display: flex;
  min-height: 282.55px;
  width: 100%;
`;

const Image = styled.img`
  border-radius: 8px;
  box-shadow:
    rgba(17, 17, 26, 0.1) 0px 4px 8px,
    rgba(17, 17, 26, 0.1) 0px 8px 12px,
    rgba(17, 17, 26, 0.1) 0px 16px 28px;
  height: auto;
  object-fit: cover;
  object-position: center;
  overflow: clip;
  width: 50%;
`;

const CloseButton = styled.button`
  ${cssResets.button}
  background-color: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 2rem;
  top: 2rem;
`;

const CloseButtonIcon = styled(CrossIcon)`
  height: 0.75rem;
  stroke: black;
  width: 0.75rem;
`;

const Mask = styled.mask`
  fill: white;
`;

const Cutout = styled.circle`
  fill: blue;
`;

const Triangle = styled.div<{ $cutout: CutoutProps; $popup: PopupProps | undefined }>`
  position: absolute;
  z-index: ${zIndex.coachmarkInactive};

  ${({ $cutout, $popup }) =>
    $popup !== undefined &&
    css`
      opacity: ${$cutout.radius === 0 ? 0 : 1};
      ${$popup.position === "above" &&
      css`
        border-left: 16px solid transparent;
        border-right: 16px solid transparent;
        border-top: calc(32px + 2px) solid ${({ theme }) => theme.colors.backgroundSecondary};
        left: ${$cutout.x}px;
        top: ${$cutout.y - $cutout.radius - 16}px;
      `}
      ${$popup.position === "right" &&
      css`
        border-bottom: 16px solid transparent;
        border-right: calc(32px + 2px) solid ${({ theme }) => theme.colors.backgroundSecondary};
        border-top: 16px solid transparent;
        left: ${$cutout.x + $cutout.radius + 16}px;
        top: ${$cutout.y}px;
      `}
        ${$popup.position === "below" &&
      css`
        border-bottom: calc(32px + 2px) solid ${({ theme }) => theme.colors.backgroundSecondary};
        border-left: 20px solid transparent;
        border-right: 20px solid transparent;
        left: ${$cutout.x}px;
        top: ${$cutout.y + $cutout.radius + 16}px;
      `}
        ${$popup.position === "left" &&
      css`
        border-bottom: 24px solid transparent;
        border-left: calc(32px + 2px) solid ${({ theme }) => theme.colors.backgroundSecondary};
        border-top: 24px solid transparent;
        left: ${$cutout.x - $cutout.radius - 16}px;
        top: ${$cutout.y}px;
      `};
    `}

  transform: translateX(-50%) translateY(-50%);
`;
